import { IAPIRoutes } from "../../../../config";

/**
 * HCHB coordination notes — notes pushed (or attempted) to Homecare Homebase
 * via the CNS Gateway. NOT the same thing as the local-only Circadia
 * "coordination notes" table that backs patient comments
 * (see coordNotesRepository).
 *
 * All HCHB routing identity (tenant, MRN, episode) is resolved server-side
 * from the patient's Compassus admission; the FE only supplies what a human
 * authored (message + note type).
 */

export type HchbPushStatus = "pending" | "success" | "failed";

/** Origin of a coordination note, used for per-surface list filtering. */
export type HchbCoordinationNoteSource =
  | "comment"
  | "care_plan"
  | "after_visit_summary";

export interface IHchbNoteType {
  id: string;
  description: string;
}

export interface IHchbNoteTypesResponse {
  tenant: string;
  note_types: IHchbNoteType[];
}

export interface IHchbCoordinationNote {
  id: number;
  clinic_id: number;
  patient_id: number;
  user_id: number;
  source?: HchbCoordinationNoteSource | null;
  hchb_tenant: string;
  hchb_patient_id: string;
  hchb_episode_id: string;
  hchb_note_type: string;
  hchb_note_type_description: string;
  note_message: string;
  note_date: string;
  note_user: string;
  push_status: HchbPushStatus;
  push_attempts: number;
  push_last_error: string | null;
  push_last_attempt_at: string | null;
  pushed_at: string | null;
  hchb_response_detail: string | null;
  status: number;
  created_at: string;
  updated_at: string;
}

export interface IHchbCoordinationNoteResponse {
  note: IHchbCoordinationNote;
}

export interface IHchbCoordinationNotesListResponse {
  notes: IHchbCoordinationNote[];
  total: number;
  limit: number;
  offset: number;
}

/** Backend-enforced maximum for every coordination-note push path. */
export const HCHB_NOTE_MESSAGE_MAX_LENGTH = 7000;

export class HchbCoordinationNotesApiError extends Error {
  readonly detail?: unknown;

  constructor(message: string, detail?: unknown) {
    super(message);
    this.name = "HchbCoordinationNotesApiError";
    this.detail = detail;
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Best-effort human message out of a FastAPI `detail` (string, validation list, or object). */
const detailMessage = (detail: unknown, fallback: string): string => {
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (!isRecord(item)) return null;
        if (typeof item.msg === "string") return item.msg;
        if (typeof item.message === "string") return item.message;
        return null;
      })
      .filter((message): message is string => Boolean(message));
    if (messages.length) return messages.join("; ");
  }
  if (isRecord(detail)) {
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.error === "string") return detail.error;
  }
  return fallback;
};

/**
 * `APIProvider` swallows non-2xx responses and returns the FastAPI error body
 * (`{ detail: ... }`) as if it were data. Without this check a 400 ("patient
 * has no episode yet"), 404 or 409 ("already being retried") from the BE
 * would reach callers as a "successful" result with no `note` in it, and the
 * UI would crash on `undefined.push_status` instead of showing the reason.
 * Throwing here makes the service layer return `{ success: false, error }`.
 */
const throwIfFastApiError = (response: unknown, fallback: string): void => {
  if (!isRecord(response) || !Object.hasOwn(response, "detail")) return;
  throw new HchbCoordinationNotesApiError(
    detailMessage(response.detail, fallback),
    response.detail
  );
};

/**
 * Anything that is neither a FastAPI error nor the expected shape (an HTML
 * gateway error page, an empty body, a proxy JSON envelope) is rejected here
 * so callers never dereference a missing `note` / `notes`.
 */
const invalidResponse = (message: string, response: unknown): never => {
  throw new HchbCoordinationNotesApiError(message, response);
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);
const isNullableString = (value: unknown): value is string | null =>
  value === null || typeof value === "string";

const isHchbNote = (value: unknown): value is IHchbCoordinationNote =>
  isRecord(value) &&
  ["id", "clinic_id", "patient_id", "user_id", "push_attempts", "status"].every(
    (field) => isFiniteNumber(value[field])
  ) &&
  [
    "hchb_tenant",
    "hchb_patient_id",
    "hchb_episode_id",
    "hchb_note_type",
    "hchb_note_type_description",
    "note_message",
    "note_date",
    "note_user",
    "created_at",
    "updated_at",
  ].every((field) => typeof value[field] === "string") &&
  [
    "push_last_error",
    "push_last_attempt_at",
    "pushed_at",
    "hchb_response_detail",
  ].every((field) => isNullableString(value[field])) &&
  (value.source === undefined ||
    value.source === null ||
    value.source === "comment" ||
    value.source === "care_plan" ||
    value.source === "after_visit_summary") &&
  (value.push_status === "pending" ||
    value.push_status === "success" ||
    value.push_status === "failed");

const isNoteResponse = (
  value: unknown
): value is IHchbCoordinationNoteResponse =>
  isRecord(value) && isHchbNote(value.note);

const isNotesListResponse = (
  value: unknown
): value is IHchbCoordinationNotesListResponse =>
  isRecord(value) &&
  Array.isArray(value.notes) &&
  value.notes.every(isHchbNote) &&
  ["total", "limit", "offset"].every((field) => isFiniteNumber(value[field]));

const isNoteTypesResponse = (value: unknown): value is IHchbNoteTypesResponse =>
  isRecord(value) &&
  typeof value.tenant === "string" &&
  Array.isArray(value.note_types) &&
  value.note_types.every(
    (item) =>
      isRecord(item) &&
      typeof item.id === "string" &&
      typeof item.description === "string"
  );

export type IHchbCoordinationNotesRepository = ReturnType<
  typeof HchbCoordinationNotesRepositoryFactory
>;

export const HchbCoordinationNotesRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}) => {
  const { GET, POST } = APIProvider;
  const { getAuthenticationToken } = AuthService;

  const getNoteTypesAPI = async ({
    clinic_id,
    patient_id,
  }: {
    clinic_id: number;
    patient_id: number;
  }) => {
    const url = APIRoutes.apiHchbCoordinationNoteTypes
      .replace("{clinic_id}", clinic_id.toString())
      .replace("{patient_id}", patient_id.toString());

    const response: unknown = await GET(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
    throwIfFastApiError(response, "Failed to load HCHB note types");
    if (!isNoteTypesResponse(response)) {
      return invalidResponse("Invalid HCHB note types response", response);
    }
    return response;
  };

  const listNotesAPI = async ({
    clinic_id,
    patient_id,
    limit = 100,
    offset = 0,
    source,
  }: {
    clinic_id: number;
    patient_id: number;
    limit?: number;
    offset?: number;
    source?: HchbCoordinationNoteSource | null;
  }) => {
    const url = APIRoutes.apiHchbCoordinationNotes
      .replace("{clinic_id}", clinic_id.toString())
      .replace("{patient_id}", patient_id.toString());

    const params: Record<string, string> = {
      limit: limit.toString(),
      offset: offset.toString(),
    };
    if (source) params.source = source;

    const response: unknown = await GET(url, params, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
    throwIfFastApiError(response, "Failed to load HCHB coordination notes");
    if (!isNotesListResponse(response)) {
      return invalidResponse(
        "Invalid HCHB coordination notes response",
        response
      );
    }
    return response;
  };

  const createNoteAPI = async ({
    clinic_id,
    patient_id,
    hchb_note_type,
    hchb_note_type_description,
    note_message,
    source,
  }: {
    clinic_id: number;
    patient_id: number;
    hchb_note_type: string;
    hchb_note_type_description?: string;
    note_message: string;
    source?: HchbCoordinationNoteSource | null;
  }) => {
    const url = APIRoutes.apiHchbCoordinationNotes
      .replace("{clinic_id}", clinic_id.toString())
      .replace("{patient_id}", patient_id.toString());

    const response: unknown = await POST(
      url,
      { hchb_note_type, hchb_note_type_description, note_message, source },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
        "Content-Type": "application/json",
      }
    );
    throwIfFastApiError(
      response,
      "Failed to send the coordination note to HCHB"
    );
    if (!isNoteResponse(response)) {
      return invalidResponse(
        "Invalid HCHB coordination note response",
        response
      );
    }
    return response;
  };

  const retryNoteAPI = async ({
    clinic_id,
    patient_id,
    coordination_note_id,
  }: {
    clinic_id: number;
    patient_id: number;
    coordination_note_id: number;
  }) => {
    const url = APIRoutes.apiRetryCoordinationNote
      .replace("{clinic_id}", clinic_id.toString())
      .replace("{patient_id}", patient_id.toString())
      .replace("{coordination_note_id}", coordination_note_id.toString());

    const response: unknown = await POST(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
        "Content-Type": "application/json",
      }
    );
    // The BE answers 400 (patient still has no MRN / episode -- sync lag),
    // 409 (already successful, soft-deleted, tenant changed, or another
    // retry holds the claim) and 404 through this path.
    throwIfFastApiError(response, "Failed to retry the HCHB coordination note");
    if (!isNoteResponse(response)) {
      return invalidResponse(
        "Invalid HCHB coordination note retry response",
        response
      );
    }
    return response;
  };

  return { getNoteTypesAPI, listNotesAPI, createNoteAPI, retryNoteAPI };
};
